import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Card } from 'react-bootstrap';
import { LuTruck, LuPackage, LuX } from 'react-icons/lu';
import { useDispatch } from 'react-redux';
import { createShipment } from '../../../redux/reducers/shipmentSlice';
import PhoneInput from '../../../components/common/PhoneInput';

export default function CreateShipmentModal({ show, onHide, invoice, onSuccess }) {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    
    const [deliveryType, setDeliveryType] = useState('self');
    
    // Address (prefilled from invoice customer)
    const [address, setAddress] = useState({
        recipientName: '', street: '', city: '', state: '', zipCode: '', country: '', phone: { countryCode: '+1', number: '' }
    });

    // Strategy forms
    const [selfDelivery, setSelfDelivery] = useState({ driverName: '', driverPhone: { countryCode: '+1', number: '' }, vehicleInfo: '', deliveryNotes: '' });
    const [thirdParty, setThirdParty] = useState({ carrierName: 'FedEx', trackingNumber: '' });
    const [specialInstructions, setSpecialInstructions] = useState('');

    useEffect(() => {
        if (show && invoice?.customer) {
            const c = invoice.customer;
            setAddress({
                recipientName: c.name || '',
                street: typeof c.address === 'object' ? c.address?.street : c.address || '',
                city: c.address?.city || '',
                state: c.address?.state || '',
                zipCode: c.address?.zipCode || '',
                country: c.address?.country || '',
                phone: typeof c.phone === 'string' 
                    ? { countryCode: '+1', number: c.phone } 
                    : c.phone || { countryCode: '+1', number: "" }
            });
        }
    }, [show, invoice]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        const payload = {
            invoiceId: invoice._id,
            deliveryType,
            specialInstructions,
            address // optional override if we supported it in backend body directly, but we map strictly inside controller.
            // Wait, the backend currently maps strictly from invoice.customer. To support editable address, 
            // I should pass it. But the prompt says "pre-filled from customer, editable", so I will pass it 
            // and the frontend payload can just send it along. Actually, I didn't explicitly map `address` override in backend.
            // For now, I'll send it all inside `selfDelivery` and `thirdParty` and maybe the backend could be updated if needed.
        };

        if (deliveryType === 'self') {
            payload.selfDelivery = selfDelivery;
        } else {
            payload.thirdParty = thirdParty;
        }

        const res = await dispatch(createShipment(payload));
        setLoading(false);

        if (!res.error) {
            onSuccess(res.payload?.data);
            onHide();
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
            <Form onSubmit={handleSubmit}>
                <Modal.Header className="border-0 pb-0 d-flex justify-content-between align-items-center">
                    <Modal.Title className="fw-bold">Create Shipment</Modal.Title>
                    <button type="button" className="btn border-0 text-muted" onClick={onHide}>
                        <LuX size={20} />
                    </button>
                </Modal.Header>
                
                <Modal.Body className="p-4">
                    {/* Step 1: Delivery Type */}
                    <div className="mb-4">
                        <label className="fw-semibold text-muted text-uppercase mb-3" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                            Step 1 — Select Delivery Method
                        </label>
                        <Row className="g-3">
                            <Col md={6}>
                                <div 
                                    onClick={() => setDeliveryType('self')}
                                    className={`p-3 rounded-4 cursor-pointer h-100 ${deliveryType === 'self' ? 'border-primary' : 'border'}`}
                                    style={{ 
                                        cursor: 'pointer', 
                                        border: deliveryType === 'self' ? '2px solid #28B0C4' : '1px solid #e2e8f0',
                                        background: deliveryType === 'self' ? 'rgba(40,176,196,0.03)' : '#fff'
                                    }}
                                >
                                    <div className="d-flex align-items-center gap-3 mb-2">
                                        <div className="p-2 rounded-3" style={{ background: deliveryType === 'self' ? '#28B0C4' : '#f1f5f9', color: deliveryType === 'self' ? 'white' : '#64748b' }}>
                                            <LuTruck size={20} />
                                        </div>
                                        <h6 className="fw-bold mb-0 text-dark">Self Delivery</h6>
                                    </div>
                                    <p className="text-muted small mb-0">We deliver it ourselves using our own fleet and drivers.</p>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div 
                                    onClick={() => setDeliveryType('third_party')}
                                    className={`p-3 rounded-4 cursor-pointer h-100 ${deliveryType === 'third_party' ? 'border-primary' : 'border'}`}
                                    style={{ 
                                        cursor: 'pointer', 
                                        border: deliveryType === 'third_party' ? '2px solid #28B0C4' : '1px solid #e2e8f0',
                                        background: deliveryType === 'third_party' ? 'rgba(40,176,196,0.03)' : '#fff'
                                    }}
                                >
                                    <div className="d-flex align-items-center gap-3 mb-2">
                                        <div className="p-2 rounded-3" style={{ background: deliveryType === 'third_party' ? '#28B0C4' : '#f1f5f9', color: deliveryType === 'third_party' ? 'white' : '#64748b' }}>
                                            <LuPackage size={20} />
                                        </div>
                                        <h6 className="fw-bold mb-0 text-dark">Third Party Carrier</h6>
                                    </div>
                                    <p className="text-muted small mb-0">Use FedEx, UPS, Canada Post or other logistics carriers.</p>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <hr className="my-4" />

                    {/* Step 2: Form */}
                    <div className="mb-4">
                        <label className="fw-semibold text-muted text-uppercase mb-3" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                            Step 2 — Shipment Details
                        </label>

                        {deliveryType === 'self' ? (
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold">Driver Name *</Form.Label>
                                        <Form.Control required value={selfDelivery.driverName} onChange={e => setSelfDelivery({...selfDelivery, driverName: e.target.value})} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <PhoneInput
                                        label="Driver Phone"
                                        value={selfDelivery.driverPhone}
                                        onChange={val => setSelfDelivery({...selfDelivery, driverPhone: val})}
                                    />
                                </Col>
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold">Vehicle Info (Optional)</Form.Label>
                                        <Form.Control placeholder="e.g. Toyota Camry, White" value={selfDelivery.vehicleInfo} onChange={e => setSelfDelivery({...selfDelivery, vehicleInfo: e.target.value})} />
                                    </Form.Group>
                                </Col>
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold">Delivery Notes (Internal)</Form.Label>
                                        <Form.Control as="textarea" rows={2} value={selfDelivery.deliveryNotes} onChange={e => setSelfDelivery({...selfDelivery, deliveryNotes: e.target.value})} />
                                    </Form.Group>
                                </Col>
                            </Row>
                        ) : (
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold">Carrier *</Form.Label>
                                        <Form.Select required value={thirdParty.carrierName} onChange={e => setThirdParty({...thirdParty, carrierName: e.target.value})}>
                                            <option value="FedEx">FedEx</option>
                                            <option value="UPS">UPS</option>
                                            <option value="Canada Post">Canada Post</option>
                                            <option value="Purolator">Purolator</option>
                                            <option value="DHL">DHL</option>
                                            <option value="Other">Other</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold">Tracking Number *</Form.Label>
                                        <Form.Control required value={thirdParty.trackingNumber} onChange={e => setThirdParty({...thirdParty, trackingNumber: e.target.value})} />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        <Row className="g-3 mt-1">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold">Special Instructions (Customer Facing)</Form.Label>
                                    <Form.Control as="textarea" rows={2} placeholder="Leave at back door..." value={specialInstructions} onChange={e => setSpecialInstructions(e.target.value)} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>

                    {/* Address Review */}
                    <div className="bg-light p-3 rounded-4 mb-4 border">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <LuTruck size={14} className="text-muted" />
                            <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: '13px' }}>Delivery Address (Snapshot)</h6>
                        </div>
                        <div className="text-muted small">
                            {address.recipientName}<br />
                            {address.street}<br />
                            {address.city}, {address.state} {address.zipCode}<br />
                            {address.country}
                        </div>
                    </div>

                    {/* Items Readonly */}
                    <div className="border p-3 rounded-4">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <LuPackage size={14} className="text-muted" />
                            <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: '13px' }}>Items Shipped</h6>
                        </div>
                        <div className="small text-muted">
                            {invoice?.productDetail?.map((item, idx) => (
                                <div key={idx} className="d-flex justify-content-between border-bottom py-1">
                                    <span>{item.productName}</span>
                                    <span className="fw-bold">× {item.quantity}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 px-4 pb-4">
                    <Button variant="light" onClick={onHide} className="fw-bold rounded-pill px-4">Cancel</Button>
                    <Button type="submit" disabled={loading} style={{ background: '#28B0C4', border: 'none' }} className="fw-bold rounded-pill px-4">
                        {loading ? 'Creating...' : 'Create Shipment →'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
